import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
    RiMenuLine,
    RiCloseLine,
    RiUserLine,
    RiFolderLine,
    RiFileList3Line,
    RiSettings3Line,
    RiShoppingCart2Line,
} from 'react-icons/ri'
import { useEffect, useState } from 'react'

import { ROUTES } from '@/constants/routes'
import { LOGO } from '@/constants/common'
import { useAuthStore } from '@/stores/useAuthStore'
import { useCartStore, selectItemCount } from '@/stores/useCartStore'
import { Button } from '@/components/ui'

import styles from './Navbar.module.css'

export function Navbar() {
    const navigate = useNavigate()
    const location = useLocation()

    const { isLogged, getUser } = useAuthStore()
    const user = getUser()
    const cartCount = useCartStore(selectItemCount)

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [transparent, setTransparent] = useState(false)
    const [lastScrollY, setLastScrollY] = useState(0)

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY
            const scrollingDown = currentScrollY > lastScrollY
            const pastThreshold = currentScrollY > 100

            if (scrollingDown && pastThreshold) {
                setHidden(true)
                setTransparent(false)
            } else if (!scrollingDown && pastThreshold) {
                setHidden(false)
                setTransparent(true)
            } else {
                setHidden(false)
                setTransparent(false)
            }

            setLastScrollY(currentScrollY)
        }

        window.addEventListener('scroll', handleScroll, { passive: true })
        return () => window.removeEventListener('scroll', handleScroll)
    }, [lastScrollY])

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = ''
        }
        return () => { document.body.style.overflow = '' }
    }, [mobileMenuOpen])

    const isActive = (path: string) => location.pathname.startsWith(path)

    const navItems = isLogged
        ? [
              { path: ROUTES.projects, icon: RiFolderLine, label: 'Proyectos' },
              { path: ROUTES.orders, icon: RiFileList3Line, label: 'Pedidos' },
              ...(user?.is_staff ? [{ path: ROUTES.adminDashboard, icon: RiSettings3Line, label: 'Admin' }] : []),
          ]
        : []

    const toggleMobileMenu = () => {
        setMobileMenuOpen((prev) => !prev)
    }

    const navbarClasses = [
        styles.navbar,
        hidden ? styles.hidden : '',
        transparent ? styles.transparent : '',
    ]
        .filter(Boolean)
        .join(' ')

    return (
        <nav className={navbarClasses}>
            {/* Logo only — no title text */}
            <Link to={ROUTES.home} className={styles.logo}>
                <img src={LOGO.src} alt={LOGO.alt} className={styles.logoImg} />
            </Link>

            {/* Center nav links (desktop) */}
            {navItems.length > 0 && (
                <div className={styles.navPill}>
                    {navItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`${styles.navLink} ${isActive(item.path) ? styles.active : ''}`}
                        >
                            <item.icon className={styles.linkIcon} />
                            <span>{item.label}</span>
                        </Link>
                    ))}
                </div>
            )}

            {/* Desktop right actions */}
            <div className={styles.actions}>
                {isLogged ? (
                    <>
                        {/* Cart icon with badge */}
                        <Link
                            to={ROUTES.cart}
                            className={styles.cartLink}
                            aria-label={`Carrito${cartCount > 0 ? ` (${cartCount} productos)` : ''}`}
                        >
                            <RiShoppingCart2Line size={22} />
                            {cartCount > 0 && (
                                <span className={styles.cartBadge}>
                                    {cartCount > 99 ? '99+' : cartCount}
                                </span>
                            )}
                        </Link>

                        <Link to={ROUTES.profile} className={styles.profileBtn} aria-label="Perfil">
                            <RiUserLine />
                        </Link>
                    </>
                ) : (
                    <div className={styles.authButtons}>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate(ROUTES.login)}
                        >
                            Iniciar sesión
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => navigate(ROUTES.register)}
                        >
                            Crear cuenta
                        </Button>
                    </div>
                )}
            </div>

            {/* Mobile toggle */}
            <button
                className={styles.mobileToggle}
                onClick={toggleMobileMenu}
                aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            >
                {mobileMenuOpen ? (
                    <RiCloseLine size={22} />
                ) : (
                    <RiMenuLine size={22} />
                )}
            </button>

            {/* Mobile menu overlay */}
            {mobileMenuOpen && (
                <div
                    className={styles.mobileOverlay}
                    onClick={() => setMobileMenuOpen(false)}
                />
            )}

            {/* Mobile menu drawer */}
            <div className={`${styles.mobileMenu} ${mobileMenuOpen ? styles.mobileOpen : ''}`}>
                <div className={styles.mobileNavLinks}>
                    {navItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`${styles.mobileLink} ${isActive(item.path) ? styles.active : ''}`}
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            <item.icon className={styles.mobileLinkIcon} />
                            <span>{item.label}</span>
                        </Link>
                    ))}

                    {/* Cart link in mobile nav */}
                    {isLogged && (
                        <Link
                            to={ROUTES.cart}
                            className={`${styles.mobileLink} ${isActive(ROUTES.cart) ? styles.active : ''}`}
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            <RiShoppingCart2Line className={styles.mobileLinkIcon} />
                            <span>Carrito</span>
                            {cartCount > 0 && (
                                <span className={styles.mobileBadge}>
                                    {cartCount > 99 ? '99+' : cartCount}
                                </span>
                            )}
                        </Link>
                    )}
                </div>

                <div className={styles.mobileFooter}>
                    {isLogged ? (
                        <Link
                            to={ROUTES.profile}
                            className={styles.mobileLink}
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            <RiUserLine className={styles.mobileLinkIcon} />
                            <span>Perfil</span>
                        </Link>
                    ) : (
                        <div className={styles.mobileAuthButtons}>
                            <Button
                                variant="primary"
                                size="md"
                                onClick={() => {
                                    navigate(ROUTES.login)
                                    setMobileMenuOpen(false)
                                }}
                            >
                                Iniciar sesión
                            </Button>
                            <Button
                                variant="secondary"
                                size="md"
                                onClick={() => {
                                    navigate(ROUTES.register)
                                    setMobileMenuOpen(false)
                                }}
                            >
                                Crear cuenta
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </nav>
    )
}
